import { uuidv7 } from "uuidv7"

import type { Conversation } from "~/models/polyschema/Conversation"

import { addConversationToBuf } from "~/utils/flatbuffers/conversation"

import { asTimestampMillis, conversationMember, dateFromTimestampUuid, groupFor, metapropertyUuid, PropertyGroup, PropertyId, PropertySchema, PropertyStatus } from "../entity"
import { BaseEntity } from "../entity/BaseEntity"
import { FileUserRole } from "../file/properties/FileUserRole"
import { AgentTurnEndedReasonBuf } from "../generated/conversation"
import { EntityPropertyGroupIdBuf } from "../generated/entity"
import { sendConversationCloseRequests } from "../handlers/conversation/close"
import { sendConversationSubscribeRequests } from "../handlers/conversation/subscribe"

export abstract class BaseConversation extends BaseEntity<ConversationProperty, Conversation> {
  static clientPreHash() {
    return `00040000-${uuidv7()}`
  }

  override toNavigationUrl(): string {
    return `/conversation/id/${asShortId(this.id)}`
  }

  addToBuf(builder: DatagramBufferBuilder) {
    return addConversationToBuf(builder, this.inner())
  }

  /** Generates the property id for the "conversation turn" property on conversations */
  static conversationTurn(timestamp: string) {
    return metapropertyUuid(
      EntityPropertyGroupIdBuf.ConversationTurnsMetaProperty,
      timestamp,
    )
  }

  memberProperties(): ConversationProperty[] {
    return Object
      .values(this.properties)
      .map(p => p!)
      .filter(p => groupFor(p.propertyId as PropertyId) === PropertyGroup.ConversationSharingUsersMetaProperty)
  }

  members(): Record<string, FileUserRole> {
    const members: Record<string, FileUserRole> = {}
    for (const property of this.memberProperties()) {
      if (property.deleted)
        continue
      if (!property.sharedWithUserId)
        continue
      if (property.value.type !== PropertySchema.RoleUuidValue)
        continue
      const role = new FileUserRole(property.value.role)
      members[property.sharedWithUserId] = role
    }
    return members
  }

  getUserRole(userId: string): FileUserRole | undefined {
    const property = this.property(conversationMember(userId))
    if (property && !property.deleted && property.value.type === PropertySchema.RoleUuidValue)
      return new FileUserRole(property.value.role)
  }

  isUserRemoved(userId: string): boolean {
    const property = this.property(conversationMember(userId))
    return property?.deleted === true
  }

  /**
   * Adds a pending owner property to this conversation
   */
  addSelfOwner(userId: string, timestamp: string) {
    this.addProperty({
      id: this.id,
      propertyId: conversationMember(userId),
      author: userId,
      schema: PropertySchema.RoleUuidValue,
      version: 0,
      sharedWithUserId: userId,
      status: PropertyStatus.StatusPending,
      value: { type: PropertySchema.RoleUuidValue, role: FileUserRole.newOwner().inner() },
      timestamp,
    })
  }

  addCreatedAt(userId: string, timestamp: string) {
    this.addProperty({
      id: this.id,
      propertyId: PropertyId.CONVERSATION_CREATED_AT,
      value: { type: PropertySchema.UtcTimestampMillisValue, utcTimestampMillis: asTimestampMillis(timestamp) },
      timestamp,
      version: 0,
      schema: PropertySchema.UtcTimestampMillisValue,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    return this
  }

  /**
   * The conversation's transcript is just a set of conversation properties, sorted
   * by timestamp in ascending order
   */
  turnProperties() {
    return Object
      .values(this.properties)
      .map(p => p!)
      .filter(p => groupFor(p.propertyId as PropertyId) === PropertyGroup.ConversationTurnsMetaProperty)
      .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
  }

  /**
   * NOTE: This does not guarantee that any particular turn will be returned, just the ones
   * that are in this object.
   */
  turns() {
    return this
      .turnProperties()
      .map(p => p.value.type === PropertySchema.ConversationTurnValue ? p.value.turn : undefined)
      .filter(p => p!)
      .map(p => p!)
  }

  artifactsDirectoryId() {
    const property = this.property(PropertyId.CONVERSATION_ARTIFACTS_FOLDER_ID)
    if (!property)
      return
    if (property.value.type === PropertySchema.FileIdValue)
      return property.value.fileId
    console.error("artifacts directory id is malformed", property)
  }

  lastUpdated() {
    const property = Object.values(this.properties)
      .map(p => p!)
      .filter(p => groupFor(p.propertyId as PropertyId) === PropertyGroup.ConversationSharingUsersMetaProperty)
      .reduce((latest, current) => {
        if (!latest || (current.timestamp > latest.timestamp))
          return current
        return latest
      }, undefined as ConversationProperty | undefined)
    if (!property)
      throw new Error("No conversation members, cannot know last updated time")
    return dateFromTimestampUuid(property.timestamp)
  }

  isEmpty() {
    return Object
      .values(this.properties)
      .map(p => p!)
      .find(p => groupFor(p.propertyId as PropertyId) === PropertyGroup.ConversationTurnsMetaProperty) === undefined
  }

  /**
   * The title of this conversation (which could be un-set)
   */
  title() {
    const titleProperty = this.property(PropertyId.CONVERSATION_USER_TITLE)
    if (titleProperty && titleProperty.value.type === PropertySchema.NonEmptyStringValue) {
      return titleProperty.value.nonEmptyString
    }
  }

  addIsDeleted(userId: string, timestamp: string) {
    this.addProperty({
      id: this.id,
      propertyId: PropertyId.CONVERSATION_IS_DELETED,
      value: { type: PropertySchema.BoolValue, bool: true },
      timestamp,
      version: 0,
      schema: PropertySchema.BoolValue,
      author: userId,
      status: PropertyStatus.StatusPending,
    })
  }

  /**
   * A conversation is closed if the last message in the transcript is an 'ended' response
   */
  isClosed() {
    if (this.selection.hasEntireGroup(PropertyGroup.ConversationTurnsMetaProperty)) {
      const turn = last(this.turns())
      if (!isDefined(turn))
        return true
      if ("agent" in turn && "ended" in turn.agent) {
        const reason = turn.agent.ended
        if (reason === AgentTurnEndedReasonBuf.ExecutedFunction)
          return false // function calls are not considered closed
        return true
      }
      return false
    }
    // cannot know if we don't have the selection. Default to assuming it is.
    return true
  }

  /**
   * TODO: throwing errors here is not an ergonomic way to handle the result. Instead, we
   * could return an option or return a tuple of [error, result]
   */
  close() {
    const { resolve, reject, promise: authoritative } = Promise.withResolvers<void>()
    const requestId = generateWorkerRequestId()
    const { addInterfaceEventListener, removeInterfaceEventListener } = useEvents()
    const onStop = (response: ConversationCloseResponseDatagram) => {
      if (response.error)
        reject(response.error)
      else
        resolve()
      removeInterfaceEventListener("conversationCloseResponse", onStop)
    }
    addInterfaceEventListener(
      "conversationCloseResponse",
      onStop,
      { requestId },
    )
    sendConversationCloseRequests([{
      requestId,
      conversationId: this.id,
      offline: false,
    }])
    return { authoritative }
  }

  async subscribe(timeoutMs = 5000) {
    const requestId = generateWorkerRequestId()
    sendConversationSubscribeRequests([{
      requestId,
      conversationId: this.id,
      offline: false,
    }])
    const { waitForFirstDataResponse } = useEvents()
    const response = await withTimeout(
      waitForFirstDataResponse("conversationSubscribeResponse", requestId),
      timeoutMs,
    )
    return response
  }
}
